import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, Mail, Phone, Truck, Check, X } from 'lucide-react';
import Modal from '../components/Modal';
import './FormPage.css';
import { API_BASE_URL, maskCnpjCpf } from '../global';
import { showAlert, showConfirm } from '../components/AlertPopup';

export default function Clients() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Transportadora Searchable Selector State
  const [carrierSearch, setCarrierSearch] = useState('');
  const [isCarrierDropdownOpen, setIsCarrierDropdownOpen] = useState(false);

  const [formData, setFormData] = useState({ 
    id: null, 
    cliente_id: '', 
    cliente_nome: '', 
    cliente_email: '', 
    cliente_telefone: '', 
    cliente_endereco: '',
    cnpj: '',
    transportadora_id: ''
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;

  useEffect(() => {
    fetchClients();
    fetchCarriers();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/clientes.php`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setClients(data);
      }
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
    }
  };

  const fetchCarriers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/transportadoras.php`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setCarriers(data);
      }
    } catch (error) {
      console.error('Erro ao buscar transportadoras:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const val = name === 'cnpj' ? maskCnpjCpf(value) : value;
    setFormData(prev => ({ ...prev, [name]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const method = formData.id ? 'PUT' : 'POST';
    
    const payload = { ...formData };
    if (!payload.id && !payload.cliente_id) {
        payload.cliente_id = 'CLI' + Math.floor(Math.random() * 10000);
    }

    try {
      const response = await fetch(`${API_BASE_URL}/clientes.php`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (result.success) {
        setIsModalOpen(false);
        fetchClients();
      } else {
        showAlert('Aviso', 'Erro ao salvar cliente');
      }
    } catch (error) {
      console.error('Erro:', error);
    }
  };

  const handleEdit = (client) => {
    setFormData({ 
      id: client.id, 
      cliente_id: client.cliente_id, 
      cliente_nome: client.cliente_nome, 
      cliente_email: client.cliente_email, 
      cliente_telefone: client.cliente_telefone, 
      cliente_endereco: client.cliente_endereco,
      cnpj: client.cnpj || '',
      transportadora_id: client.transportadora_id || ''
    });

    const assignedCarrier = carriers.find(c => String(c.id) === String(client.transportadora_id));
    setCarrierSearch(assignedCarrier ? assignedCarrier.nome : '');
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm('Confirmação', 'Tem certeza que deseja excluir este cliente?');
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/clientes.php?id=${id}`, {
        method: 'DELETE'
      });
      const result = await response.json();
      if (result.success) {
        fetchClients();
      } else {
        showAlert('Aviso', 'Erro ao excluir cliente');
      }
    } catch (error) {
      console.error('Erro:', error);
    }
  };

  const openNewModal = () => {
    setFormData({ 
      id: null, 
      cliente_id: '', 
      cliente_nome: '', 
      cliente_email: '', 
      cliente_telefone: '', 
      cliente_endereco: '',
      cnpj: '',
      transportadora_id: ''
    });
    setCarrierSearch('');
    setIsModalOpen(true);
  };

  const handleSelectCarrier = (carrier) => {
    setFormData(prev => ({ ...prev, transportadora_id: carrier.id }));
    setCarrierSearch(carrier.nome);
    setIsCarrierDropdownOpen(false);
  };

  const handleClearCarrier = () => {
    setFormData(prev => ({ ...prev, transportadora_id: '' }));
    setCarrierSearch('');
    setIsCarrierDropdownOpen(false);
  };

  const filteredCarriers = carriers.filter(c =>
    (c.nome || '').toLowerCase().includes(carrierSearch.toLowerCase()) ||
    (c.cnpj_cpf || '').includes(carrierSearch) ||
    (c.cidade || '').toLowerCase().includes(carrierSearch.toLowerCase())
  );

  const filteredClients = clients.filter(client => 
    (client.cliente_nome && client.cliente_nome.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (client.cliente_email && client.cliente_email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (client.cliente_telefone && client.cliente_telefone.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (client.transportadora_nome && client.transportadora_nome.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalPages = Math.ceil(filteredClients.length / itemsPerPage) || 1;
  const paginatedClients = filteredClients.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="page-container animate-fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Clientes</h1>
          <p className="page-subtitle">Gerencie o cadastro de clientes e suas transportadoras vinculadas</p>
        </div>
        <button className="btn-primary" onClick={openNewModal} style={{ width: 'auto', padding: '0.75rem 1.25rem' }}>
          <Plus size={18} /> Novo Cliente
        </button>
      </div>

      <div className="card list-card">
        <div className="card-header">
          <div className="search-bar" style={{ width: '100%', maxWidth: '400px' }}>
            <Search size={18} className="search-icon" />
            <input 
              type="text" 
              placeholder="Buscar cliente por nome, email ou transportadora..." 
              className="search-input" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Contato</th>
                <th>Transportadora Vinculada</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {paginatedClients.map((client) => (
                <tr key={client.id}>
                  <td>
                    <div className="font-medium" style={{ color: 'var(--text-main)', fontWeight: '600' }}>{client.cliente_nome}</div>
                    <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                      {client.cnpj ? `CNPJ/CPF: ${client.cnpj}` : `ID: ${client.cliente_id}`} (Ref: #{client.id})
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)' }}>
                        <Mail size={14} /> {client.cliente_email}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)' }}>
                        <Phone size={14} /> {client.cliente_telefone}
                      </div>
                    </div>
                  </td>
                  <td>
                    {client.transportadora_nome ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--primary-color)', fontWeight: '500', fontSize: '0.85rem' }}>
                        <Truck size={16} /> {client.transportadora_nome}
                      </div>
                    ) : (
                      <span className="text-muted" style={{ fontSize: '0.8rem' }}>Nenhuma vinculada</span>
                    )}
                  </td>
                  <td>
                    <span className="badge badge-success">Ativo</span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon" title="Editar" onClick={() => handleEdit(client)}><Edit2 size={16} /></button>
                      <button className="btn-icon danger" title="Excluir" onClick={() => handleDelete(client.id)}><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredClients.length === 0 && (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--text-muted)' }}>
                    Nenhum cliente encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* CONTROLES DE PAGINAÇÃO */}
        {filteredClients.length > 0 && (
          <div style={{
            display: 'flex',
            justify: 'space-between',
            alignItems: 'center',
            padding: '1rem 1.5rem',
            borderTop: '1px solid var(--border-color)',
            flexWrap: 'wrap',
            gap: '0.75rem'
          }}>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              Exibindo {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, filteredClients.length)} de {filteredClients.length} clientes
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                className="btn-outline"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                style={{ padding: '0.35rem 0.75rem', fontSize: '0.85rem', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
              >
                Anterior
              </button>
              <span style={{ fontSize: '0.85rem', fontWeight: '500', padding: '0 0.5rem', color: 'var(--text-main)' }}>
                Página {currentPage} de {totalPages}
              </span>
              <button
                className="btn-outline"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                style={{ padding: '0.35rem 0.75rem', fontSize: '0.85rem', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>

      {/* MODAL CADASTRO / EDIÇÃO */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        title={formData.id ? "Editar Cliente" : "Novo Cliente"}
      >
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%' }}>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <div className="form-group flex-2">
              <label>Nome ou Razão Social <span className="text-danger">*</span></label>
              <input type="text" name="cliente_nome" value={formData.cliente_nome} onChange={handleInputChange} placeholder="Ex: Empresa Exemplo Ltda" required className="standard-input" />
            </div>
            <div className="form-group flex-1">
              <label>CNPJ ou CPF</label>
              <input type="text" name="cnpj" value={formData.cnpj} onChange={handleInputChange} placeholder="00.000.000/0000-00" className="standard-input" />
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <div className="form-group flex-1">
              <label>E-mail Corporativo <span className="text-danger">*</span></label>
              <input type="email" name="cliente_email" value={formData.cliente_email} onChange={handleInputChange} placeholder="contato@empresa.com" required className="standard-input" />
            </div>
            <div className="form-group flex-1" style={{ maxWidth: '180px' }}>
              <label>Telefone</label>
              <input type="tel" name="cliente_telefone" value={formData.cliente_telefone} onChange={handleInputChange} placeholder="(00) 0000-0000" className="standard-input" />
            </div>
          </div>

          <div className="form-group">
            <label>Endereço Completo</label>
            <input type="text" name="cliente_endereco" value={formData.cliente_endereco} onChange={handleInputChange} placeholder="Rua, Número, Bairro, Cidade - UF" className="standard-input" />
          </div>

          {/* SELETOR PESQUISÁVEL DE TRANSPORTADORA */}
          <div className="form-group" style={{ position: 'relative' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <Truck size={16} color="var(--primary-color)" /> Atribuir Transportadora
            </label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input
                type="text"
                className="standard-input"
                placeholder="Digite o nome da transportadora para pesquisar..."
                value={carrierSearch}
                onChange={(e) => {
                  setCarrierSearch(e.target.value);
                  setIsCarrierDropdownOpen(true);
                }}
                onFocus={() => setIsCarrierDropdownOpen(true)}
                style={{ paddingRight: '2.5rem' }}
              />
              {formData.transportadora_id ? (
                <button
                  type="button"
                  onClick={handleClearCarrier}
                  style={{ position: 'absolute', right: '10px', background: 'none', border: 'none', color: 'var(--danger-color)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                  title="Remover vinculação"
                >
                  <X size={18} />
                </button>
              ) : (
                <Search size={18} style={{ position: 'absolute', right: '10px', color: 'var(--text-muted)', pointerEvents: 'none' }} />
              )}
            </div>

            {/* DROPDOWN MENU DE PESQUISA */}
            {isCarrierDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: '#ffffff',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                boxShadow: 'var(--shadow-lg)',
                maxHeight: '180px',
                overflowY: 'auto',
                zIndex: 1000,
                marginTop: '4px'
              }}>
                {filteredCarriers.length > 0 ? (
                  filteredCarriers.map((c) => (
                    <div
                      key={c.id}
                      onClick={() => handleSelectCarrier(c)}
                      style={{
                        padding: '0.6rem 0.85rem',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderBottom: '1px solid var(--border-subtle)',
                        backgroundColor: String(formData.transportadora_id) === String(c.id) ? 'var(--primary-light)' : 'transparent',
                        transition: 'background-color 0.15s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--primary-light)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = String(formData.transportadora_id) === String(c.id) ? 'var(--primary-light)' : 'transparent'}
                    >
                      <div>
                        <div style={{ fontWeight: '600', color: 'var(--text-main)', fontSize: '0.85rem' }}>{c.nome}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                          CNPJ: {c.cnpj_cpf || 'Não inf.'} {c.cidade ? `| ${c.cidade}/${c.uf}` : ''}
                        </div>
                      </div>
                      {String(formData.transportadora_id) === String(c.id) && (
                        <Check size={16} color="var(--primary-color)" />
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    Nenhuma transportadora encontrada.
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-actions" style={{ justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border-color)' }}>
            <button type="button" className="btn-outline" onClick={() => setIsModalOpen(false)}>Cancelar</button>
            <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '0.6rem 1.25rem' }}>Salvar Cliente</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
